/* eslint-disable max-lines */
import Sidebar from "@/components/Sidebar/SidebarView.vue";
import HomeNavBar from "@/components/HomeNavBar/HomeNavBar.vue";
import InactivityModal from "@/components/InactivityTracker/InactivityTracker.vue";
import { get_token } from "@/router";
import swal from "sweetalert2";

import {
  format_currency,
  format_date,
} from "@/views/DonationCampaigns/donation_campaigns_methods_helper";
import {
  DONATION_CERTIFICATE_COUNTRY_OPTIONS,
  get_certificate_form_errors,
  get_certificate_registration_type_options,
  get_empty_certificate_form,
  get_empty_certificate_form_errors,
  normalize_certificate_registration_number,
  normalize_certificate_sars_tax_reference_number,
} from "@/views/DonationHistory/donation_history_certificate_helper";

function get_normalized_status(entry) {
  return String(entry?.donation_status || "").trim().toLowerCase();
}

export default {
  name: "DonationHistoryView",
  components: {
    Sidebar,
    HomeNavBar,
    InactivityModal,
  },
  data() {
    return ({
      donations: [],
      is_loading: false,
      country_options: DONATION_CERTIFICATE_COUNTRY_OPTIONS,
      certificate_form: get_empty_certificate_form(),
      certificate_form_errors: get_empty_certificate_form_errors(),
      selected_certificate_donation: null,
      is_generating_certificate: false,
    });
  },
  computed: {
    total_donated_amount() {
      return this.donations
        .filter((donation) => get_normalized_status(donation) === "completed")
        .reduce((total, donation) => total + Number(donation?.amount || 0), 0);
    },
    total_donations_count() {
      return this.donations.length;
    },
    completed_donations_count() {
      return this.donations.filter(
        (donation) => get_normalized_status(donation) === "completed"
      ).length;
    },
    pending_donations_count() {
      return this.donations.filter(
        (donation) => get_normalized_status(donation) === "pending"
      ).length;
    },
    certificate_registration_type_options() {
      return get_certificate_registration_type_options(this.certificate_form.donor_type);
    },
  },
  mounted() {
    this.fetch_donation_history();
  },
  methods: {
    toggleSidebar() {
      this.$refs.sidebar.$el.classList.toggle("close");
    },
    getToken() {
      return get_token();
    },
    get_request_headers() {
      return ({
        Authorization: `Token ${this.getToken()}`,
      });
    },
    init_donations_datatable() {
      const table_id = "#donationHistoryTable";
      const table = $(table_id);

      if (!table.length || !$.fn.DataTable) {
        return;
      }

      if ($.fn.DataTable.isDataTable(table_id)) {
        table.DataTable().clear().destroy();
      }

      table.DataTable({
        paging: true,
        searching: true,
        ordering: true,
        info: true,
        lengthChange: false,
        autoWidth: false,
        responsive: false,
        pageLength: 10,
        order: [[6, "desc"]],
        language: {
          paginate: {
            previous: "Previous",
            next: "Next",
          },
          info: "Showing _START_ to _END_ of _TOTAL_ entries",
          infoEmpty: "No entries available",
          emptyTable: "No entries available",
          search: "Search:",
        },
      });
    },
    fetch_donation_history() {
      this.is_loading = true;

      $.ajax({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/donation_management/get_my_donations/`,
        method: "GET",
        headers: this.get_request_headers(),
        success: (response) => {
          this.donations = Array.isArray(response?.data) ? response.data : [];
          this.is_loading = false;

          this.$nextTick(() => {
            this.init_donations_datatable();
          });
        },
      });
    },
    open_campaign(donation) {
      if (!donation?.campaign_id) {
        return;
      }

      this.$router.push({
        name: "donation_campaigns",
        query: { campaign_id: donation.campaign_id },
      });
    },
    has_certificate(donation) {
      return Boolean(donation?.certificate?.file?.file_url);
    },
    can_show_certificate_actions(donation) {
      return this.get_donation_status(donation) === "completed" && Number(donation?.amount || 0) >= 250;
    },
    download_certificate(donation)  {
      const file_url = donation?.certificate?.file?.file_url || "";
      if (!file_url) {
        return;
      }

      this.open_file_in_new_tab(file_url);
    },
    reset_certificate_form() {
      this.certificate_form = get_empty_certificate_form();
      this.certificate_form_errors = get_empty_certificate_form_errors();
    },
    validate_certificate_form() {
      const next_errors = get_certificate_form_errors(this.certificate_form);
      this.certificate_form_errors = {
        ...get_empty_certificate_form_errors(),
        ...next_errors,
      };
      return !Object.values(next_errors).some(Boolean);
    },
    open_generate_certificate_modal(donation) {
      if (!donation?.id) {
        return;
      }

      this.selected_certificate_donation = donation;
      this.reset_certificate_form();

      this.$nextTick(() => {
        $("#generateCertificateModal").modal("show");
      });
    },
    open_file_in_new_tab(file_url) {
      if (!file_url) {
        return;
      }

      const new_tab = window.open(file_url, "_blank");
      if (!new_tab) {
        window.location = file_url;
      }
    },
    close_generate_certificate_modal() {
      $("#generateCertificateModal").modal("hide");
      this.selected_certificate_donation = null;
      this.reset_certificate_form();
      this.is_generating_certificate = false;
    },
    handle_certificate_donor_type_change() {
      const next_registration_type = this.certificate_registration_type_options[0]?.value || "";
      this.certificate_form = {
        ...this.certificate_form,
        registration_type: next_registration_type,
        registration_number: "",
      };
      this.certificate_form_errors = {
        ...this.certificate_form_errors,
        donor_type: "",
        registration_type: "",
        registration_number: "",
      };
    },
    handle_certificate_registration_number_input() {
      this.certificate_form = {
        ...this.certificate_form,
        registration_number: normalize_certificate_registration_number(this.certificate_form.registration_number, this.certificate_form.registration_type),
      };
      this.certificate_form_errors = {
        ...this.certificate_form_errors,
        registration_number: "",
      };
    },
    handle_certificate_sars_tax_reference_number_input() {
      this.certificate_form = {
        ...this.certificate_form,
        sars_tax_reference_number: normalize_certificate_sars_tax_reference_number(this.certificate_form.sars_tax_reference_number),
      };
      this.certificate_form_errors = {
        ...this.certificate_form_errors,
        sars_tax_reference_number: "",
      };
    },
    submit_generate_certificate() {
  if (!this.selected_certificate_donation?.id || this.is_generating_certificate) {
    return;
  }

  if (!this.validate_certificate_form()) {
    return;
  }

  const donation = this.selected_certificate_donation;
  const form_data = new FormData();

  form_data.append("contribution_id", donation.id);
  form_data.append("donor_type", this.certificate_form.donor_type);
  form_data.append("registration_type", this.certificate_form.registration_type);
  form_data.append("registration_number", this.certificate_form.registration_number);
  form_data.append("country_of_issue", this.certificate_form.country_of_issue);
  form_data.append(
    "sars_tax_reference_number",
    this.certificate_form.sars_tax_reference_number
  );

  this.is_generating_certificate = true;

  $.ajax({
    url: `${process.env.VUE_APP_BACKEND_API_URL}/donation_management/generate_donation_certificate/`,
    method: "POST",
    headers: this.get_request_headers(),
    data: form_data,
    processData: false,
    contentType: false,
    success: (response) => this.handle_certificate_success(response, donation),
    error: (xhr) => this.handle_certificate_error(xhr),
  });
},

handle_certificate_success(response, donation) {
  this.is_generating_certificate = false;

  const file_url = response?.data?.file_url || "";
  const file_name =
    response?.data?.file_name || "Section 18A Certificate.pdf";

  donation.certificate = {
    ...(donation.certificate || {}),
    file: {
      ...(donation?.certificate?.file || {}),
      name: file_name,
      file_url,
    },
  };

  this.close_generate_certificate_modal();
  this.open_file_in_new_tab(file_url);
},

handle_certificate_error(xhr) {
  this.is_generating_certificate = false;

  const message =
    xhr?.responseJSON?.message || "Could not generate certificate.";

  swal.fire({
    icon: "error",
    title:
      typeof message === "string"
        ? message
        : "Could not generate certificate.",
    showConfirmButton: false,
    timer: 2400,
    position: "bottom-end",
    toast: true,
  });
},
    get_donation_status(donation) {
      const status = String(donation?.donation_status || "").trim().toLowerCase();
      if (status === "pending") {
        return "failed";
      }

      return status || "failed";
    },
    get_donation_message(donation) {
      return String(donation?.message || "").trim() || "No message";
    },
    get_donation_date(donation) {
      return donation?.date_donated || "";
    },
    format_currency,
    format_date,
  },
};
